import { takeLatest, call, put, select, cancelled } from 'redux-saga/effects';
import i18next from 'i18next';
import {
  loadIpmaCisInvoiceCertificatesCreator,
  setIpmaCisBarCreator,
  setIpmaCisCreateErrorsCreator,
  setIpmaCisCreator,
  setIpmaCisDetailsCreator,
  setIpmaCisEditErrorsCreator,
  setIpmaCisOrganizationCertificationBodyLevelDomainsCreator,
  setIpmaCisInvoiceCertificatesCreator,
  setIpmaCisPagesCreator,
  setMaCisBarCreator,
  setMaCisCreateErrorsCreator,
  setMaCisCreator,
  setMaCisEditErrorsCreator,
  setMaCisPagesCreator,
  setClientMemberAssociationsCreator,
  setIpmaCisTotalItemsCreator,
  setMaCisTotalItemsCreator,
  setIpmaCisCreatedCreator,
  setMaCisCreatedCreator,
  setMaCisDetailsCreator,
  importDataErrorsCreator,
  loadIpmaCisCreator,
  loadMaCisCreator,
  loadIpmaCisInvoiceInvoicedCertificatesCreator,
  loadIpmaCisInvoiceUninvoicedCertificatesCreator,
  setGlobalCisClear,
  setAllowedCbLevelDomainsCreator,
} from '../store/actionsCreators';

import * as cisService from 'api/services/cis';

import {
  LOAD_MA_CIS,
  LOAD_MA_CIS_BAR,
  CREATE_MA_CIS,
  UPDATE_MA_CIS,
  REMOVE_MA_CIS,
  LOAD_IPMA_CIS,
  LOAD_IPMA_CIS_BAR,
  CREATE_IPMA_CIS,
  UPDATE_IPMA_CIS,
  REMOVE_IPMA_CIS,
  LOAD_IPMA_CIS_DETAILS,
  LOAD_IPMA_CIS_ORGANIZATION_CERTIFICATION_BODY_LEVEL_DOMAINS,
  LOAD_IPMA_CIS_INVOICE_CERTIFICATES,
  ATTACH_IPMA_CIS_INVOICE_CERTIFICATES,
  DETACH_IPMA_CIS_INVOICE_CERTIFICATES,
  LOAD_CBLESS_MEMBER_ASSOCIATIONS,
  LOAD_MA_CIS_DETAILS,
  LOAD_IPMA_CIS_INVOICE_INVOICED_CERTIFICATES,
  LOAD_IPMA_CIS_INVOICE_UNINVOICED_CERTIFICATES,
  APPROVE_MA_CHANGE_REQUEST,
  APPROVE_IPMA_CHANGE_REQUEST,
} from 'store/constants';

import {
  setNewAlertCreator,
  setNewLoadingCreator,
  removeLoadingCreator,
  setCblessMemberAssociationsCreator,
  loadCblessMemberAssociationsCreator,
} from 'store/actionsCreators';
import { parseParams, parseBoxesList } from 'helpers';
import {
  ACCEPT_CIS_IMPORT,
  REJECT_CIS_IMPORT,
  IMPORT_CIS_DATA,
  LOAD_CLIENT_MEMBER_ASSOCIATIONS_CB,
  LOAD_CLIENT_MEMBER_ASSOCIATIONS_MA,
  TOGGLE_CERTIFICATION_BODY,
  LOAD_MA_CIS_ALLOWED_CB_LEVEL_DOMAINS,
  VERIFY_IPMA_CHANGE_REQUEST,
  IMPLEMENT_IPMA_CHANGE_REQUEST,
} from '../store/constants';

function replaceUpperWithDashLower(text) {
  return text.replace(/[A-Z]/g, (match) => '-' + match.toLowerCase());
}

function* loadIpmaCis({ cisType }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ipma }) => {
      return [
        ipma.cis[cisType].filters,
        ipma.cis[cisType].page,
        ipma.cis[cisType].perPage,
        ipma.cis[cisType].sort,
      ];
    });

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(cisService.getCisList, replaceUpperWithDashLower(cisType), params);
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setIpmaCisCreator(cisType, items));
      yield put(setIpmaCisPagesCreator(cisType, pageCount || 1));
      yield put(setIpmaCisTotalItemsCreator(cisType, totalItems));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log(`[GET] ipma cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* importData(payload) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.importData, payload);
    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();
      if (result.errors && result.errors.length) {
        yield put(importDataErrorsCreator(result.errors));
        yield put(
          setNewAlertCreator({
            type: 'cis',
            status: 'error',
            text: i18next.t(`cis.importFailed`),
          }),
        );
      } else if (result.unknown > 0) {
        yield put(
          setNewAlertCreator({
            type: 'cis',
            status: 'success',
            text: i18next.t(`cis.importUnknown`) + `(${result.unknown})`,
          }),
        );
      } else {
        yield put(
          setNewAlertCreator({
            type: 'cis',
            status: 'success',
            text: i18next.t(`global.importSuccess`),
          }),
        );
      }
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setIpmaCisCreateErrorsCreator(
          payload.type,
          Object.fromEntries(
            Object.entries(errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[GET] import cis ${payload.type} error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* createIpmaCis({ cisType, payload, history, redirectOnSuccess }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.create, replaceUpperWithDashLower(cisType), payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.createSuccess`),
        }),
      );
      if (cisType === 'certificateHolders') {
        const {
          results: { result },
        } = yield response.json();
        yield put(
          setIpmaCisCreatedCreator(cisType, {
            value: result,
            label: `${payload.firstName} ${payload.lastName}`,
          }),
        );
      }
      if (cisType === 'certificationBodies') {
        yield put(loadCblessMemberAssociationsCreator());
      }
      if (cisType === 'levelDomains') {
        yield put(setGlobalCisClear('levelDomains'));
        yield put(setGlobalCisClear('levelDomainsLevels'));
        yield put(setGlobalCisClear('levelDomainsDomains'));
      }
      if (cisType === 'certificationBodies' || cisType === 'organizations') {
        yield put(setGlobalCisClear(cisType));
      }
    }
    if (redirectOnSuccess)
      history.push(
        `/Ipma/${cisType === 'reports' ? 'cvmb-cis' : 'cis'}/${replaceUpperWithDashLower(cisType)}`,
      );
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setIpmaCisCreateErrorsCreator(
          cisType,
          Object.fromEntries(
            Object.entries(errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[GET] create cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* updateIpmaCis({ id, cisType, payload, history }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.update, id, replaceUpperWithDashLower(cisType), payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.updateSuccess`),
        }),
      );
    }
    history.push(
      `/Ipma/${cisType === 'reports' ? 'cvmb-cis' : 'cis'}/${replaceUpperWithDashLower(cisType)}`,
    );
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setIpmaCisEditErrorsCreator(
          cisType,
          Object.fromEntries(
            Object.entries(errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[GET] update cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* removeIpmaCis({ cisType, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.remove, payload, replaceUpperWithDashLower(cisType));
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.removeSuccess`),
        }),
      );
      window.location.reload();
    }
  } catch (error) {
    const { errors } = yield error.response.json();

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        list: errors,
      }),
    );
    console.log(`[DELETE] remove cis ${cisType} error: `, error);
  }
}

function* loadIpmaCisBar({ payload }) {
  try {
    if (payload && !payload.withoutLoader) yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(cisService.getCisBar);
    if (response.ok) {
      const {
        results: {
          result: { entity: bar },
        },
      } = yield response.json();
      yield put(
        setIpmaCisBarCreator({
          certificationBodies: bar.certificationBodies || 0,
          cetificateHolders: bar.cetificateHolders || 0,
          certificates: bar.certificates || 0,
          levelDomains: bar.levelDomains || 0,
          invoices: bar.invoices || 0,
          agreements: bar.agreements || 0,
          organizations: bar.organizations || 0,
          reports: bar.reports || 0,
        }),
      );
    }
    if (payload && !payload.withoutLoader) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    if (payload && !payload.withoutLoader) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] Ipma cis bar error: ', error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* loadIpmaCisDetails({ cisType, payload: id, history }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(cisService.getCisDetails, replaceUpperWithDashLower(cisType), id);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaCisDetailsCreator(cisType, result));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));

    if (history) {
      history.push('/404');
    }

    console.log(`[GET] get cis ${cisType} details error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadIpmaCisOrganizationCertificationBodyLevelDomains({ payload: id }) {
  try {
    yield put(setNewLoadingCreator({ type: 'LOAD_IPMA_CIS_ORGANIZATION_CB_LD' }));

    const response = yield call(cisService.getOrganizationCertificationBodyLevelDomains, id);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setIpmaCisOrganizationCertificationBodyLevelDomainsCreator(result));
    }
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_ORGANIZATION_CB_LD' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_ORGANIZATION_CB_LD' }));

    console.log(`[GET] get cis organization's certification body level domains: `, error);
  } finally {
    if (yield cancelled())
      yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_ORGANIZATION_CB_LD' }));
  }
}

function* loadIpmaCisInvoiceCertificates({ payload }) {
  yield put(loadIpmaCisInvoiceInvoicedCertificatesCreator(payload));
  yield put(loadIpmaCisInvoiceUninvoicedCertificatesCreator(payload));
}

function* loadIpmaCisInvoiceInvoicedOrUninvoicedCertificates({ type, payload: id }) {
  const invoiced = type === LOAD_IPMA_CIS_INVOICE_INVOICED_CERTIFICATES;
  const cisType = invoiced ? 'invoicedCertificates' : 'uninvoicedCertificates';
  try {
    yield put(setNewLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));

    const [filters, page, perPage, sort] = yield select(({ ipma }) => {
      return [
        ipma.cis[cisType].filters,
        ipma.cis[cisType].page,
        ipma.cis[cisType].perPage,
        ipma.cis[cisType].sort,
      ];
    });

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(cisService.getInvoiceCertificates, id, invoiced, params);

    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setIpmaCisCreator(cisType, items));
      yield put(setIpmaCisPagesCreator(cisType, pageCount || 1));
      yield put(setIpmaCisTotalItemsCreator(cisType, totalItems));
    }

    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    console.log(`[GET] ipma cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled())
      yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
  }
}

function* attachIpmaCisInvoiceCertificates({ id, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    const body = JSON.stringify({
      certificateIds: payload,
    });
    const response = yield call(cisService.attachInvoiceCertificates, id, body);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: `cis-invoices`,
          status: 'success',
          text: i18next.t(`cis.invoices.attachCertificatesSuccess`),
        }),
      );
    }
    yield put(loadIpmaCisInvoiceCertificatesCreator(id));
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setIpmaCisCreateErrorsCreator(
          'invoices',
          Object.fromEntries(Object.entries(errors).map(([key, value]) => [key, value[0]])),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: `cis-invoices`,
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[POST] attach certificates to invoice error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    }
  }
}

function* detachIpmaCisInvoiceCertificates({ id, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    const body = JSON.stringify({
      certificateIds: payload,
    });
    const response = yield call(cisService.detachInvoiceCertificates, body);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: `cis-invoices`,
          status: 'success',
          text: i18next.t(`cis.invoices.detachCertificatesSuccess`),
        }),
      );
    }
    yield put(loadIpmaCisInvoiceCertificatesCreator(id));
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setIpmaCisCreateErrorsCreator(
          'invoices',
          Object.fromEntries(Object.entries(errors).map(([key, value]) => [key, value[0]])),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: `cis-invoices`,
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[POST] detach certificates from invoice error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES' }));
    }
  }
}

function* loadMaCis({ cisType }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => {
      return [
        ma.cis[cisType].filters,
        ma.cis[cisType].page,
        ma.cis[cisType].perPage,
        ma.cis[cisType].sort,
      ];
    });

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(
        cisService.getMaCisList,
        replaceUpperWithDashLower(cisType),
        params,
      );
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      const boxList = cisType === 'certificationBodies';

      yield put(setMaCisCreator(cisType, boxList ? parseBoxesList(cisType, items, true) : items));
      yield put(setMaCisPagesCreator(cisType, pageCount || 1));
      yield put(setMaCisTotalItemsCreator(cisType, totalItems));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log(`[GET] ma cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadMaCisDetails({ cisType, payload: id, history }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(cisService.getMaCisDetails, replaceUpperWithDashLower(cisType), id);

    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();

      yield put(setMaCisDetailsCreator(cisType, result));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));

    if (history) {
      history.push('/404');
    }

    console.log(`[GET] get ma cis ${cisType} details error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* createMaCis({ cisType, payload, history, redirectOnSuccess }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.maCreate, replaceUpperWithDashLower(cisType), payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.createSuccess`),
        }),
      );
      if (cisType === 'certificateHolders') {
        const {
          results: { result },
        } = yield response.json();
        yield put(
          setMaCisCreatedCreator(cisType, {
            value: result,
            label: `${payload.firstName} ${payload.lastName}`,
          }),
        );
      }
      if (cisType === 'certificationBodies') {
        yield put(loadCblessMemberAssociationsCreator());
      }
      if (cisType === 'reports') {
        yield put(loadMaCisCreator('reports'));
      }
      if (cisType === 'levelDomains') {
        yield put(setGlobalCisClear('levelDomains'));
        yield put(setGlobalCisClear('levelDomainsLevels'));
        yield put(setGlobalCisClear('levelDomainsDomains'));
      }
      if (cisType === 'certificationBodies' || cisType === 'organizations') {
        yield put(setGlobalCisClear(cisType));
      }
    }
    if (redirectOnSuccess) history.push(`/ma/cis/${replaceUpperWithDashLower(cisType)}`);
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setMaCisCreateErrorsCreator(
          cisType,
          Object.fromEntries(
            Object.entries(errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[GET] create ma cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* updateMaCis({ id, cisType, payload, history, redirectOnSuccess = true }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(
      cisService.maUpdate,
      id,
      replaceUpperWithDashLower(cisType),
      payload,
    );
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.updateSuccess`),
        }),
      );
    }
    if (redirectOnSuccess) {
      history.push(`/ma/cis/${replaceUpperWithDashLower(cisType)}`);
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(
        setMaCisEditErrorsCreator(
          cisType,
          Object.fromEntries(
            Object.entries(errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ]),
          ),
        ),
      );
    } else {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'error',
          list: errors,
        }),
      );
    }
    console.log(`[GET] update ma cis ${cisType} error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* removeMaCis({ cisType, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.maRemove, payload, replaceUpperWithDashLower(cisType));
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.${cisType}.removeSuccess`),
        }),
      );
        window.location.reload();
    }
  } catch (error) {
    const { errors } = yield error.response.json();

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        list: errors,
      }),
    );
    console.log(`[DELETE] remove ma cis ${cisType} error: `, error);
  }
}

function* loadMaCisBar({ payload }) {
  try {
    if (payload && !payload.withoutLoader) yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const response = yield call(cisService.getMaCisBar);
    if (response.ok) {
      const {
        results: {
          result: { entity: bar },
        },
      } = yield response.json();
      yield put(
        setMaCisBarCreator({
          cetificateHolders: bar.cetificateHolders || 0,
          certificates: bar.certificates || 0,
        }),
      );
    }
    if (payload && !payload.withoutLoader) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    if (payload && !payload.withoutLoader) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] Ipma ma cis bar error: ', error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* loadClientMAByCB({ payload }) {
  try {
    const response = yield call(cisService.getClientMAByCB, payload);
    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();
      yield put(setClientMemberAssociationsCreator(result));
    }
  } catch (e) {
    console.log('Error loading client MA by CB:', e);
    yield put(setClientMemberAssociationsCreator([]));
  }
}

function* loadClientMAByMA({ payload }) {
  try {
    const response = yield call(cisService.getClientMAByMA, payload);
    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();
      yield put(setClientMemberAssociationsCreator(result));
    }
  } catch (e) {
    console.log('Error loading client MA by MA:', e);
    yield put(setClientMemberAssociationsCreator([]));
  }
}

function* loadCblessMemberAssociations() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.getCblessMAs);

    if (response.ok) {
      const memberAssociations = yield response.json();
      yield put(
        setCblessMemberAssociationsCreator(
          memberAssociations.results.result.map((item) => ({
            value: item.id,
            label: item.name,
          })),
        ),
      );
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] global cbless member associations error: ', error);
  }
}

function* acceptCisImport({ payload, profile }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.acceptCisImport, payload);
    if (response.ok) {
      if (profile === 'ipma') {
        yield put(loadIpmaCisCreator('unknownImports'));
      } else if (profile === 'ma') {
        yield put(loadMaCisCreator('unknownImports'));
      }
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.importAccepted`),
        }),
      );
    }
  } catch (error) {
    const { errors } = yield error.response.json();
    let errList = Object.entries(errors).map(([key, value]) => [
      `${key}: `,
      Array.isArray(value) ? value[0] : value,
    ]);

    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        list: errList,
      }),
    );
  } finally {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* rejectCisImport({ payload, profile }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.rejectCisImport, payload);
    if (response.ok) {
      if (profile === 'ipma') {
        yield put(loadIpmaCisCreator('unknownImports'));
      } else if (profile === 'ma') {
        yield put(loadMaCisCreator('unknownImports'));
      }
    }
  } catch (e) {
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(`cis.importRejectFailed`),
      }),
    );
  } finally {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* toggleCertificationBodySuspended({ payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.toggleCertificationBodySuspended, payload);
    if (response.ok) {
      yield put(loadIpmaCisCreator('certificationBodies'));
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.certificationBodyToggled`),
        }),
      );
    }
  } catch (error) {
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(`cis.certificationBodyToggleFailed`),
      }),
    );
  } finally {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadAllowedCbLevelDomains({ payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.getAllowedLevelDomainsByCB, payload);
    if (response.ok) {
      const {
        results: { result: ids },
      } = yield response.json();

      if (ids && ids.length) {
        yield put(setAllowedCbLevelDomainsCreator(ids));
      }
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log(`[GET] ma allowed cb level domains error: `, error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

// Change Request Status Update Sagas (specific to change requests)
function* approveMaChangeRequest({ id, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.approveChangeRequest, id, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.changeRequests.approveSuccess`),
        }),
      );
      yield put(loadMaCisCreator('changeRequests'));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(`cis.changeRequests.approveError`),
      }),
    );
    console.log(`[POST] approve ma change request error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* approveIpmaChangeRequest({ id, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.approveChangeRequest, id, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.changeRequests.approveSuccess`),
        }),
      );
      yield put(loadIpmaCisCreator('changeRequests'));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(`cis.changeRequests.approveError`),
      }),
    );
    console.log(`[POST] approve ipma change request error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

function* verifyIpmaChangeRequest({ id, payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.verifyChangeRequest, id, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(`cis.changeRequests.verifySuccess`),
        }),
      );
      yield put(loadIpmaCisCreator('changeRequests'));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(`cis.changeRequests.verifyError`),
      }),
    );
  }
}

function* implementIpmaChangeRequest({ id, payload }) {
  const isAbandon = payload?.action === 'abandon';
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(cisService.implementChangeRequest, id, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cis',
          status: 'success',
          text: i18next.t(
            isAbandon ? `cis.changeRequests.abandonSuccess` : `cis.changeRequests.implementSuccess`,
          ),
        }),
      );
      yield put(loadIpmaCisCreator('changeRequests'));
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    yield put(
      setNewAlertCreator({
        type: 'cis',
        status: 'error',
        text: i18next.t(
          isAbandon ? `cis.changeRequests.abandonError` : `cis.changeRequests.implementError`,
        ),
      }),
    );
    console.log(`[POST] implement ipma change request error: `, error);
  } finally {
    if (yield cancelled()) {
      yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    }
  }
}

export default function* watchCis() {
  yield takeLatest(LOAD_IPMA_CIS_BAR, loadIpmaCisBar);
  yield takeLatest(LOAD_IPMA_CIS, loadIpmaCis);
  yield takeLatest(CREATE_IPMA_CIS, createIpmaCis);
  yield takeLatest(UPDATE_IPMA_CIS, updateIpmaCis);
  yield takeLatest(REMOVE_IPMA_CIS, removeIpmaCis);
  yield takeLatest(LOAD_IPMA_CIS_DETAILS, loadIpmaCisDetails);
  yield takeLatest(
    LOAD_IPMA_CIS_ORGANIZATION_CERTIFICATION_BODY_LEVEL_DOMAINS,
    loadIpmaCisOrganizationCertificationBodyLevelDomains,
  );
  yield takeLatest(LOAD_IPMA_CIS_INVOICE_CERTIFICATES, loadIpmaCisInvoiceCertificates);
  yield takeLatest(ATTACH_IPMA_CIS_INVOICE_CERTIFICATES, attachIpmaCisInvoiceCertificates);
  yield takeLatest(DETACH_IPMA_CIS_INVOICE_CERTIFICATES, detachIpmaCisInvoiceCertificates);

  yield takeLatest(LOAD_CLIENT_MEMBER_ASSOCIATIONS_CB, loadClientMAByCB);
  yield takeLatest(LOAD_CLIENT_MEMBER_ASSOCIATIONS_MA, loadClientMAByMA);

  yield takeLatest(VERIFY_IPMA_CHANGE_REQUEST, verifyIpmaChangeRequest);
  yield takeLatest(IMPLEMENT_IPMA_CHANGE_REQUEST, implementIpmaChangeRequest);

  yield takeLatest(LOAD_MA_CIS_BAR, loadMaCisBar);
  yield takeLatest(LOAD_MA_CIS, loadMaCis);
  yield takeLatest(CREATE_MA_CIS, createMaCis);
  yield takeLatest(UPDATE_MA_CIS, updateMaCis);
  yield takeLatest(REMOVE_MA_CIS, removeMaCis);
  yield takeLatest(LOAD_MA_CIS_DETAILS, loadMaCisDetails);
  yield takeLatest(LOAD_CBLESS_MEMBER_ASSOCIATIONS, loadCblessMemberAssociations);
  yield takeLatest(IMPORT_CIS_DATA, importData);
  yield takeLatest(ACCEPT_CIS_IMPORT, acceptCisImport);
  yield takeLatest(REJECT_CIS_IMPORT, rejectCisImport);
  yield takeLatest(
    LOAD_IPMA_CIS_INVOICE_INVOICED_CERTIFICATES,
    loadIpmaCisInvoiceInvoicedOrUninvoicedCertificates,
  );
  yield takeLatest(
    LOAD_IPMA_CIS_INVOICE_UNINVOICED_CERTIFICATES,
    loadIpmaCisInvoiceInvoicedOrUninvoicedCertificates,
  );
  yield takeLatest(TOGGLE_CERTIFICATION_BODY, toggleCertificationBodySuspended);
  yield takeLatest(LOAD_MA_CIS_ALLOWED_CB_LEVEL_DOMAINS, loadAllowedCbLevelDomains);
  
  // Change Request Status Update Sagas (specific to change requests)
  yield takeLatest(APPROVE_MA_CHANGE_REQUEST, approveMaChangeRequest);
  yield takeLatest(APPROVE_IPMA_CHANGE_REQUEST, approveIpmaChangeRequest);
}
